import { type FC, type ReactNode, useCallback } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { buildTeamWageSettingsUrl, type WageSettingsTarget } from "../team/WageSettings/deepLink";
import { BreakdownRouteLink } from "./BreakdownRouteLink";

/** DEFAULT: what the click reports as `section` when the link opens the tab without landing on one. */
const WHOLE_TAB_SECTION = "all";

type Props = {
	/** Where on Wage Settings to land. Omitted: open the tab at the top (the footer's "Rate rules" link). */
	target?: WageSettingsTarget;
	/** Already translated, or a backend label (a rule name) shown as written. */
	label: ReactNode;
	/** Already translated: names the destination, which the visible label alone does not. */
	ariaLabel: string;
	dataTestId: string;
	/**
	 * DEFAULT: `strong` — the existing callers' look, unchanged — for a link standing on its own. `subtle`
	 * for a rule name inside a breakdown line, where a column of bold links would outshout the figures the
	 * line exists to explain.
	 */
	emphasis?: "strong" | "subtle";
	/** The configuration rows behind the link (a `RuleCard`), shown on hover and focus. Omitted: no card. */
	hoverCard?: ReactNode;
};

/**
 * A piece of a wage breakdown, linked to the Wage Settings section (and row) that produced it — the
 * route from "this number looks wrong" to the setting behind it. Callers decide whether the viewer may
 * follow it.
 */
export const WageSettingsLink: FC<Props> = ({ target, label, ariaLabel, dataTestId, emphasis = "strong", hoverCard }) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const section = target?.section ?? WHOLE_TAB_SECTION;

	const handleClick = useCallback(() => {
		track("wage_breakdown_open_wage_settings", { department_id: departmentId, section });
	}, [departmentId, section, track]);

	return (
		<BreakdownRouteLink
			to={buildTeamWageSettingsUrl(departmentId, target)}
			destination="wage_settings"
			label={label}
			ariaLabel={ariaLabel}
			onClick={handleClick}
			dataTestId={dataTestId}
			dataSection={section}
			emphasis={emphasis}
			hoverCard={hoverCard}
		/>
	);
};
